import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  CheckCircle, 
  AlertCircle, 
  ArrowLeft, 
  Loader2,
  Mail,
  RefreshCw
} from 'lucide-react';
import { Button, GlassCard } from './ui';

function EmailVerificationPage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    verifyEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`/api/verify-email/${token}`);
      
      if (response.data.message) {
        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.email || '');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      
      if (error.response?.data?.error) {
        const errorMsg = error.response.data.error;
        
        if (errorMsg.includes('expired')) {
          setStatus('expired');
          setMessage('Verification link has expired');
          // Extract email from error response if available
          if (error.response.data.email) {
            setEmail(error.response.data.email);
          }
        } else if (errorMsg.includes('invalid')) {
          setStatus('error');
          setMessage('Invalid verification link');
        } else {
          setStatus('error');
          setMessage(errorMsg);
        }
      } else {
        setStatus('error');
        setMessage('Verification failed. Please try again.');
      }
    }
  };

  const handleResendVerification = async () => {
    // If we don't have the email, prompt the user
    if (!email) {
      const userEmail = prompt('Please enter your email address to resend the verification link:');
      if (!userEmail) {
        return;
      }
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userEmail)) {
        setStatus('error');
        setMessage('Please enter a valid email address.');
        return;
      }
      
      setEmail(userEmail);
      await resendVerificationRequest(userEmail);
    } else {
      await resendVerificationRequest(email);
    }
  };

  const resendVerificationRequest = async (userEmail) => {
    setResending(true);
    try {
      const response = await axios.post('/api/resend-verification', { email: userEmail });
      
      if (response.data.message) {
        setStatus('success');
        setMessage('New verification email sent! Please check your inbox.');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setStatus('error');
      setMessage(error.response?.data?.error || 'Failed to resend verification email.');
    } finally {
      setResending(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-16 h-16 text-emerald-400" />;
      case 'error':
      case 'expired':
        return <AlertCircle className="w-16 h-16 text-red-400" />;
      case 'loading':
        return <Loader2 className="w-16 h-16 text-emerald-400 animate-spin" />;
      default:
        return <Mail className="w-16 h-16 text-zinc-400" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'bg-emerald-500/10 border-emerald-500/30';
      case 'error':
      case 'expired':
        return 'bg-red-500/10 border-red-500/30';
      case 'loading':
        return 'bg-blue-500/10 border-blue-500/30';
      default:
        return 'bg-zinc-800/50 border-zinc-700/50';
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 relative flex items-center justify-center overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />

      {/* Back Button */}
      <motion.button
        onClick={() => navigate('/login')}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-zinc-400 hover:text-zinc-100 transition-colors"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ x: -4 }}
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-medium">Back to Login</span>
      </motion.button>

      {/* Verification Card */}
      <motion.div 
        className="relative z-10 w-full max-w-md mx-4"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
      >
        <GlassCard className="p-8 backdrop-blur-2xl bg-zinc-900/60 border-zinc-800/70 shadow-2xl shadow-black/50">
          {/* Logo */}
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <img 
              src="/echokit-logo.png" 
              alt="EchoKit" 
              className="h-16 w-auto mx-auto mb-6"
            />
            <h1 className="text-2xl font-bold text-zinc-100 mb-2">
              Email Verification
            </h1>
            <p className="text-zinc-400">
              {status === 'loading' ? 'Verifying your email...' : 'Email Verification Status'}
            </p>
          </motion.div>

          {/* Status Icon */}
          <motion.div 
            className="flex justify-center mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            {getStatusIcon()}
          </motion.div>

          {/* Status Message */}
          <motion.div 
            className={`p-4 rounded-xl border ${getStatusColor()} mb-6`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-center text-sm">
              {message}
            </p>
          </motion.div>

          {/* Action Buttons */}
          <motion.div 
            className="space-y-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {status === 'success' && (
              <Button
                size="lg"
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Continue to Login
              </Button>
            )}

            {(status === 'error' || status === 'expired') && (
              <>
                <Button
                  size="lg"
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="w-full"
                >
                  Back to Login
                </Button>
                
                {status === 'expired' && (
                  <Button
                    size="lg"
                    onClick={handleResendVerification}
                    loading={resending}
                    className="w-full"
                    icon={RefreshCw}
                  >
                    {resending ? 'Sending...' : 'Resend Verification Email'}
                  </Button>
                )}
              </>
            )}

            {status === 'loading' && (
              <div className="text-center">
                <p className="text-zinc-400 text-sm">
                  Please wait while we verify your email address...
                </p>
              </div>
            )}
          </motion.div>

          {/* Help Text */}
          <motion.div 
            className="mt-8 p-4 bg-zinc-800/30 border border-zinc-700/50 rounded-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-center text-sm text-zinc-400">
              <span className="text-emerald-400 font-medium">Need help?</span> If you're having trouble verifying your email, please contact support or check your spam folder.
            </p>
          </motion.div>
        </GlassCard>
      </motion.div>
    </div>
  );
}

export default EmailVerificationPage;
